// Literal class strings only — Tailwind's JIT scans for complete strings, so
// dynamic concatenation like `bg-${accent}-600` would NOT be compiled. `hex`
// is for charts (recharts needs a color value, not a class).

// `solidText` is the text color to pair with `solid` (bg-*-600) so contrast
// passes WCAG AA (4.5:1) in both light and dark mode (the bg-*-600 value
// itself doesn't change between modes, so one choice per accent covers both).
// Verified via relative-luminance calc: blue/violet/rose-600 are too dark for
// near-black text (3.1-3.8:1 with text-neutral-900) so they get white;
// emerald/amber/cyan-600 are too light for white text (3.2-3.8:1) so they get
// near-black. Use `${accent.solid} ${accent.solidText}` anywhere a button/pill
// puts text directly on the solid accent background — do not hardcode
// text-white or text-neutral-900 dark:text-white next to accent.solid.

export const ACCENTS = {
  blue:    { name: 'Blue',    hex: '#3b82f6', solid: 'bg-blue-600 hover:bg-blue-500', solidText: 'text-white',       text: 'text-blue-500 dark:text-blue-400',    activeTab: 'bg-blue-600 text-white',           soft: 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-950/30 dark:border-blue-900/50 dark:text-blue-300',       dot: 'bg-blue-500 dark:bg-blue-400',    swatch: 'bg-blue-500' },
  emerald: { name: 'Emerald', hex: '#10b981', solid: 'bg-emerald-600 hover:bg-emerald-500', solidText: 'text-neutral-900', text: 'text-emerald-600 dark:text-emerald-400', activeTab: 'bg-emerald-600 text-neutral-900', soft: 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/30 dark:border-emerald-900/50 dark:text-emerald-300', dot: 'bg-emerald-500 dark:bg-emerald-400', swatch: 'bg-emerald-500' },
  violet:  { name: 'Violet',  hex: '#8b5cf6', solid: 'bg-violet-600 hover:bg-violet-500', solidText: 'text-white',   text: 'text-violet-600 dark:text-violet-400',  activeTab: 'bg-violet-600 text-white',         soft: 'bg-violet-50 border-violet-200 text-violet-700 dark:bg-violet-950/30 dark:border-violet-900/50 dark:text-violet-300',   dot: 'bg-violet-500 dark:bg-violet-400',  swatch: 'bg-violet-500' },
  amber:   { name: 'Amber',   hex: '#f59e0b', solid: 'bg-amber-600 hover:bg-amber-500', solidText: 'text-neutral-900',     text: 'text-amber-600 dark:text-amber-400',   activeTab: 'bg-amber-600 text-neutral-900',   soft: 'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-950/30 dark:border-amber-900/50 dark:text-amber-300',     dot: 'bg-amber-500 dark:bg-amber-400',   swatch: 'bg-amber-500' },
  rose:    { name: 'Rose',    hex: '#f43f5e', solid: 'bg-rose-600 hover:bg-rose-500', solidText: 'text-white',       text: 'text-rose-600 dark:text-rose-400',    activeTab: 'bg-rose-600 text-white',           soft: 'bg-rose-50 border-rose-200 text-rose-700 dark:bg-rose-950/30 dark:border-rose-900/50 dark:text-rose-300',        dot: 'bg-rose-500 dark:bg-rose-400',    swatch: 'bg-rose-500' },
  cyan:    { name: 'Cyan',    hex: '#06b6d4', solid: 'bg-cyan-600 hover:bg-cyan-500', solidText: 'text-neutral-900',       text: 'text-cyan-600 dark:text-cyan-400',    activeTab: 'bg-cyan-600 text-neutral-900',    soft: 'bg-cyan-50 border-cyan-200 text-cyan-700 dark:bg-cyan-950/30 dark:border-cyan-900/50 dark:text-cyan-300',        dot: 'bg-cyan-500 dark:bg-cyan-400',    swatch: 'bg-cyan-500' },
};

export const FONTS = {
  sans:  { name: 'Sans',  cls: 'font-sans' },
  serif: { name: 'Serif', cls: 'font-serif' },
  mono:  { name: 'Mono',  cls: 'font-mono' },
};

export const DENSITY = {
  comfortable: { name: 'Comfortable', cell: 'px-3 py-2.5', head: 'px-3 py-2',   img: 'w-9 h-9' },
  compact:     { name: 'Compact',     cell: 'px-3 py-1.5', head: 'px-3 py-1.5', img: 'w-7 h-7' },
};

// categorical palette for multi-slice charts (pie)
export const CHART_PALETTE = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#f43f5e', '#06b6d4', '#64748b', '#eab308'];

// status-pill colors for conditional formatting (enum cells)
// Each value includes both light and dark mode classes
export const BADGE = {
  neutral: 'bg-neutral-200 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
  blue:    'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300',
  emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300',
  amber:   'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300',
  rose:    'bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300',
  violet:  'bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300',
  cyan:    'bg-cyan-100 text-cyan-700 dark:bg-cyan-950/50 dark:text-cyan-300',
};
export const BADGE_COLORS = Object.keys(BADGE);

export const DEFAULT_THEME = { accent: 'blue', font: 'sans', density: 'comfortable' };

export const resolveTheme = (theme = {}) => ({
  accent: ACCENTS[theme.accent] || ACCENTS.blue,
  font: FONTS[theme.font] || FONTS.sans,
  density: DENSITY[theme.density] || DENSITY.comfortable,
});

// '#3b82f6' -> '59 130 246' — the space-separated triplet form index.css's
// --accent-rgb (and every other CSS-var-backed color in index.css's
// `@theme inline` block) expects: Tailwind reads it as `rgb(var(--accent-rgb))`
// and applies an opacity modifier (`ring-accent/40`) through color-mix().
export const hexToRgbTriplet = (hex) => {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
  if (!m) return '59 130 246';
  return [1, 2, 3].map((i) => parseInt(m[i], 16)).join(' ');
};
